import { useQuery } from "@tanstack/react-query";
import {
  getProjectUsage,
  isProRequiredError,
  type ToolUsage,
  type ProjectUsage,
} from "../lib/api";
import { ProUpgradeNudge } from "./ProUpgradeNudge";

const WINDOW_DAYS = 30;

/** Short relative label for a recent timestamp, e.g. "3h ago", "Apr 24". */
function relativeShort(iso: string | null): string {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/**
 * Pro-gated usage analytics for a project. Renders a header total (with the
 * public-MCP split when present) and a per-tool list with a tiny inline daily
 * sparkline. On a `pro_required` error it shows the upgrade nudge instead.
 */
export function UsagePane({ slug }: { slug: string }) {
  const usage = useQuery<ProjectUsage>({
    queryKey: ["app-usage", slug, WINDOW_DAYS],
    queryFn: () => getProjectUsage(slug, WINDOW_DAYS),
    enabled: !!slug,
    retry: false,
  });

  if (usage.isLoading) {
    return (
      <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
        Loading…
      </div>
    );
  }

  if (isProRequiredError(usage.error)) {
    return (
      <div className="flex-1 min-h-0 rounded-md bg-bg-1 border border-bg-2 overflow-auto p-3">
        <div className="text-fg-2 text-xs mb-3">
          See how often this project's tools get called — including via your
          public MCP endpoint.
        </div>
        <ProUpgradeNudge error={usage.error} />
      </div>
    );
  }

  if (usage.error) {
    return (
      <div className="flex-1 min-h-0 rounded-md bg-bg-1 border border-bg-2 overflow-auto">
        <div className="p-3 text-red-400 text-xs font-mono">
          {usage.error instanceof Error
            ? usage.error.message
            : String(usage.error)}
        </div>
      </div>
    );
  }

  const data = usage.data;
  if (!data) return null;

  return (
    <div className="flex-1 min-h-0 rounded-md bg-bg-1 border border-bg-2 overflow-auto">
      <div className="px-3 py-2.5 border-b border-bg-2">
        <div className="text-fg-0 text-sm">
          <span className="font-semibold">{data.totalCalls.toLocaleString()}</span>{" "}
          tool call{data.totalCalls === 1 ? "" : "s"}
          <span className="text-fg-2"> · last {data.windowDays} days</span>
        </div>
        {data.publicCalls > 0 && (
          <div className="text-fg-2 text-xs mt-0.5">
            {data.publicCalls.toLocaleString()} via public MCP
          </div>
        )}
      </div>

      {data.tools.length === 0 ? (
        <div className="p-4 text-fg-1 text-sm">
          No calls yet — connect this project's MCP and trigger a tool.
        </div>
      ) : (
        <ul className="divide-y divide-bg-2">
          {data.tools.map((t) => (
            <ToolRow key={t.toolSlug} tool={t} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ToolRow({ tool }: { tool: ToolUsage }) {
  const max = Math.max(1, ...tool.daily.map((d) => d.count));
  return (
    <li className="px-3 py-2.5">
      <div className="flex items-baseline gap-2">
        <code className="font-mono text-xs text-fg-0 truncate flex-1 min-w-0">
          {tool.toolSlug}
        </code>
        <span className="text-fg-2 text-[10px] font-mono shrink-0">
          {relativeShort(tool.lastCalledAt)}
        </span>
        <span className="text-fg-0 text-xs font-semibold shrink-0 tabular-nums">
          {tool.total.toLocaleString()}
        </span>
        {tool.publicTotal > 0 && (
          <span className="text-accent text-[10px] font-mono shrink-0">
            {tool.publicTotal.toLocaleString()} public
          </span>
        )}
      </div>
      <Sparkline daily={tool.daily} max={max} />
    </li>
  );
}

function Sparkline({
  daily,
  max,
}: {
  daily: ToolUsage["daily"];
  max: number;
}) {
  if (daily.length === 0) return null;
  return (
    <div
      className="mt-1.5 flex items-end gap-px h-6"
      title={`${daily.length}-day daily activity`}
      aria-hidden
    >
      {daily.map((d) => {
        // Scale each day to the tool's max; floor a called day at ~2px so a
        // single call is still visible, and keep empty days as a hairline.
        const pct = d.count === 0 ? 0 : Math.max(0.12, d.count / max);
        return (
          <div
            key={d.date}
            className="flex-1 min-w-px rounded-sm"
            style={{
              height: `${Math.round(pct * 100)}%`,
              backgroundColor: d.count > 0 ? "var(--color-accent)" : "var(--color-bg-2)",
              opacity: d.count > 0 ? 0.85 : 1,
            }}
            title={`${d.date}: ${d.count} call${d.count === 1 ? "" : "s"}`}
          />
        );
      })}
    </div>
  );
}
